import React, { useState, useEffect } from "react";
import MfaModalDialog from "../../MfaModalDialog/MfaModalDialog"; // Adjust path if needed
import {
  PulseButton,
  PulseDropdown,
  PulseSpinnerIcon,
} from "@pulselabs-ai/shared-fe-ui";

import "./MfaChangeFeedbackFormModal.scss"; // Optional: Create if specific styling is needed
import { ChangeFormIcon } from "../../icons/ChangeFormIcon";
import { AddFormIcon } from "../../icons/AddFormIcon";
import TriggerButton from "../../TriggerButton/TriggerButton";
import TriggerInfoPopover from "../../TriggerInfoPopover/TriggerInfoPopover"; // Adjusted path again
import MfaFeedbackService, {
  FeedbackFormResponse,
} from "../../../services/MfaFeedbackService"; // Import Response type
import { useAppSelector } from "../../../store/hooks"; // Import useAppSelector
import MfaCaptureMethodService, {
  CaptureMethodSurveyInput,
} from "@pl-flight-recorder/web/services/MfaCaptureMethodService";
import { ActionType } from "@pl-flight-recorder/common/models/enum/action-type.enum";

interface MfaChangeFeedbackFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerId: string; // Pass the project ID for context if needed
  triggerName: string;
  triggerType: string;
  projectId: string; // Add projectId prop
  currentSurveyId: string; // Add optional prop
  onChanged: () => void;
}

const MfaChangeFeedbackFormModal: React.FC<MfaChangeFeedbackFormModalProps> = ({
  isOpen,
  onClose,
  onChanged,
  triggerId,
  triggerName,
  triggerType,
  projectId,
  currentSurveyId,
}) => {
  // State to track the selected card option
  const [selectedOption, setSelectedOption] = useState<"select" | "create">(
    "select",
  );

  // State and handlers for TriggerInfoPopover
  const [triggerAnchorEl, setTriggerAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const handleTriggerOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTriggerAnchorEl(event.currentTarget);
  };
  const handleCloseTriggerPopover = () => {
    setTriggerAnchorEl(null);
  };
  const isTriggerInfoOpen = Boolean(triggerAnchorEl);

  // State for feedback forms - Use the response type directly
  const [feedbackForms, setFeedbackForms] = useState<FeedbackFormResponse[]>(
    [],
  ); // Changed type
  const [isLoadingForms, setIsLoadingForms] = useState<boolean>(false);
  const [formsError, setFormsError] = useState<string | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string>("");

  // Get user and workspaceId from Redux store
  const { user } = useAppSelector((state) => state.user);
  const token = user?.token;
  const workspaceId = user?.defaultWorkspace?.id?.toString(); // Get workspaceId
  const userId = user?.id?.toString(); // Get userId if needed for service calls

  // Fetch feedback forms when modal opens or projectId changes
  useEffect(() => {
    if (isOpen && projectId && token) {
      const fetchForms = async () => {
        setIsLoadingForms(true);
        setFormsError(null);
        setFeedbackForms([]); // Clear previous forms
        try {
          console.log(`Fetching forms for project: ${projectId}`);
          const service = MfaFeedbackService.getInstance();
          const formsResponse = await service.getFeedbackFormsByProject(
            token,
            projectId,
          );

          // No mapping needed here, store the raw response
          setFeedbackForms(formsResponse);

          console.log("formsResponse", formsResponse);

          // Set initial selected ID based on currentSurveyId or default
          const initialId =
            currentSurveyId &&
            formsResponse.some(
              (form) => String(form.surveyId) === currentSurveyId,
            )
              ? currentSurveyId
              : formsResponse.length > 0
                ? String(formsResponse[0].surveyId)
                : "";
          setSelectedFormId(initialId);
        } catch (err) {
          console.error("Failed to fetch feedback forms:", err);
          setFormsError(
            err instanceof Error ? err.message : "Could not load forms.",
          );
        } finally {
          setIsLoadingForms(false);
        }
      };
      fetchForms();
    } else {
      // Reset when modal closes or data is missing
      setSelectedFormId("");
      setFeedbackForms([]);
      setIsLoadingForms(false);
      setFormsError(null);
    }
  }, [isOpen, projectId, token, currentSurveyId]); // Add currentSurveyId to dependency array

  // Define selected colors
  const defaultColor = "#A1A1A1";
  const selectedIconColor = "#4C4CF6"; // Purple for selected AddFormIcon (or white if background turns purple)

  // Map fetched forms (now FeedbackFormResponse) to dropdown options
  const dropdownOptions = feedbackForms.map((form) => ({
    label: form.displayName || form.schema?.title || "Untitled Form", // Use displayName or title
    value: String(form.surveyId), // Use surveyId, ensure string
  }));

  const handleSave = async () => {
    if (!token || !workspaceId || !userId) {
      console.error("Missing token, workspaceId, or userId for save operation");
      // Optionally set an error state to show the user
      return;
    }

    if (selectedOption === "select") {
      console.log("Updating trigger to use selected form:", selectedFormId);

      // Find the full form data from the state (already FeedbackFormResponse)
      const selectedForm = feedbackForms.find(
        (form) => String(form.surveyId) === selectedFormId,
      );
      console.log("selectedForm", selectedForm);

      // Check if form and schema exist
      if (!selectedForm || !selectedForm.schema) {
        console.error("Selected form or its schema not found.");
        return;
      }

      try {
        const mfaCaptureMethodService = MfaCaptureMethodService.getInstance();

        // TODO: Fetch current capture method data to get existing values if needed
        // const currentCaptureData = await mfaCaptureMethodService.getCaptureMethod(parseInt(triggerId, 10));
        // This requires getCaptureMethod to exist and work correctly.
        // For now, we'll use potentially hardcoded/assumed values for status/actionType.

        // Construct the payload matching CaptureMethodInput
        const payload: CaptureMethodSurveyInput = {
          projectId: projectId,
          actionType: triggerType as ActionType,
          status: "ACTIVE",
          screenRecordingConf: {},
          surveyId: selectedFormId,
        };

        await mfaCaptureMethodService.updateFeedbackForm(
          triggerId,
          payload,
          token,
          workspaceId,
        );
        console.log("Successfully updated capture method with new form.");
        onChanged();
        onClose(); // Close modal on success
      } catch (error) {
        console.error("Failed to update capture method:", error);
        // Optionally set an error state to show the user
      }
    } else {
      console.log(
        "Create new form option selected - save logic not implemented here.",
      );
      // TODO: Implement logic to open SurveyForm for creation or handle differently
      onChanged();
      onClose(); // Close modal for now
    }
  };

  console.log(triggerType, triggerId, triggerName);

  return (
    <MfaModalDialog isOpen={isOpen} onClose={onClose}>
      <div className="mfa-modal-dialog">
        <div className="mfa-modal-dialog-container">
          <div className="headline-xl">Change feedback form</div>

          {/* Container for name and trigger button */}
          <div className="trigger-details-container mfa-bottom-padding">
            <div className="sub-headline-xs">{triggerName}</div>
            {/* Wrap TriggerButton in a div for styling and click handling */}
            <div
              className="modal-trigger-button-wrapper"
              onClick={handleTriggerOpen} // Open popover on click
            >
              <TriggerButton
                triggerType={triggerType}
                onClick={() => {}} // Button click itself does nothing now, wrapper handles it
                status="ACTIVE"
              />
            </div>
          </div>

          <div className="card-button-container">
            <div
              className={`card-button card-button-select ${selectedOption === "select" ? "selected" : ""}`}
              onClick={() => setSelectedOption("select")}
            >
              <div>
                <ChangeFormIcon
                  className="card-icon"
                  fill={
                    selectedOption === "select"
                      ? selectedIconColor
                      : defaultColor
                  }
                />
                <div className="label-m --color-text-primary">
                  Select existing form
                </div>
              </div>
              {/* Conditionally render dropdown based on loading/error */}
              {isLoadingForms ? (
                <div className="form-loader">
                  <PulseSpinnerIcon />
                </div>
              ) : formsError ? (
                <div className="form-error body-s --color-text-error">
                  {formsError}
                </div>
              ) : (
                <PulseDropdown
                  options={dropdownOptions} // Use dynamic options
                  selectedValue={selectedFormId} // Use state for selected value
                  onChange={(value) => setSelectedFormId(value)} // Update state on change
                  placeholder="Select Form"
                  disabled={selectedOption !== "select"} // Disable if create is selected
                />
              )}
            </div>
            <div
              className={`card-button card-button-create ${selectedOption === "create" ? "selected" : ""}`}
              onClick={() => setSelectedOption("create")}
            >
              <div>
                <AddFormIcon
                  className="card-icon"
                  fill={
                    selectedOption === "create"
                      ? selectedIconColor
                      : defaultColor
                  }
                />
                <div className="label-m --color-text-primary">
                  Create new form
                </div>
              </div>
            </div>
            {/* <div className="body-s mfa-top-padding">coming soon</div> */}
          </div>
        </div>
        <div className="mfa-modal-dialog-footer">
          <PulseButton
            // disabled={
            //   workspaceName.length === 0 ||
            //   workspaceName.length > 20 ||
            //   isLoading
            // }
            onClick={handleSave}
            // loading={isLoading}
          >
            Save
          </PulseButton>
        </div>
      </div>

      {/* Render the TriggerInfoPopover */}
      <TriggerInfoPopover
        anchorEl={triggerAnchorEl}
        open={isTriggerInfoOpen}
        onClose={handleCloseTriggerPopover}
        triggerType={triggerType}
        customOffset={[0, 8]}
      />
    </MfaModalDialog>
  );
};

export default MfaChangeFeedbackFormModal;
